import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { ManualCircuitBreakerAction } from '../../api/systemApi';

// 권위: docs/ARCH_DASHBOARD.md §4.5 "수동 Circuit Breaker 발동 UI".
// 2-단계 확인 프로세스:
//   1) reason 입력 (최소 10자)
//   2) "나는 ~의 영향을 이해한다" 확인 체크박스
// 두 조건 모두 충족되면 "발동" 버튼 활성.
// submit → onConfirm(action, reason) — 컨테이너가 API 호출.
// 운용 사고 위험이 크므로 UI 상 위험 수준에 따라 색 강도 차등 (warn/danger).

export interface ManualCircuitBreakerModalProps {
  action: ManualCircuitBreakerAction;
  onClose: () => void;
  onConfirm: (action: ManualCircuitBreakerAction, reason: string) => Promise<void> | void;
  busy?: boolean;
  error?: string | null;
}

const MIN_REASON = 10;

const ACTION_LABEL: Record<ManualCircuitBreakerAction, string> = {
  HALT_NEW_ORDERS: '신규 주문 차단 (HALT_NEW_ORDERS)',
  REDUCE_ALL_50PCT: '전 포지션 50% 축소 (REDUCE_ALL_50PCT)',
  HALT_ALL: '전체 주문 차단 (HALT_ALL)',
};

const ACTION_TONE: Record<ManualCircuitBreakerAction, 'warn' | 'danger' | 'critical'> = {
  HALT_NEW_ORDERS: 'warn',
  REDUCE_ALL_50PCT: 'danger',
  HALT_ALL: 'critical',
};

const ACTION_HINT: Record<ManualCircuitBreakerAction, string> = {
  HALT_NEW_ORDERS:
    '신규 매수/매도 주문이 전면 차단됩니다. 기존 보유 포지션은 유지됩니다.',
  REDUCE_ALL_50PCT:
    '보유 중인 모든 포지션을 50% 규모로 즉시 축소합니다. 가격 슬리피지 발생 가능.',
  HALT_ALL:
    '모든 매매가 즉시 중단됩니다. 수동 해제 전까지 트레이딩 에이전트는 주문을 발행하지 않습니다.',
};

export function ManualCircuitBreakerModal({
  action,
  onClose,
  onConfirm,
  busy,
  error,
}: ManualCircuitBreakerModalProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const reasonRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // 포커스 빨리 — 사고 대응 시 즉시 입력 가능하도록.
    reasonRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const tone = ACTION_TONE[action];
  const reasonOk = reason.trim().length >= MIN_REASON;
  const canSubmit = reasonOk && confirmed && !busy;

  return (
    <div
      className="syslog-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className={`live-cb-modal live-cb-modal--${tone}`}
        role="dialog"
        aria-label="수동 Circuit Breaker 발동 확인"
        aria-modal="true"
      >
        <header className={`live-cb-modal__header live-cb-modal__header--${tone}`}>
          <div className="live-cb-modal__title-group">
            <span className="live-cb-modal__stamp">MANUAL CB</span>
            <h2 className="live-cb-modal__title">정말 이 조치를 발동하시겠습니까?</h2>
          </div>
          <button
            type="button"
            className="syslog-modal__close"
            onClick={onClose}
            disabled={busy}
            aria-label="닫기"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>

        <div className="live-cb-modal__body">
          <dl className="live-cb-modal__meta">
            <dt>조치</dt>
            <dd>
              <strong className={`live-cb-modal__action-name live-cb-modal__action-name--${tone}`}>
                {ACTION_LABEL[action]}
              </strong>
            </dd>
            <dt>영향</dt>
            <dd>{ACTION_HINT[action]}</dd>
          </dl>

          <label className="live-cb-modal__field">
            <span className="live-cb-modal__label">
              발동 사유 (최소 {MIN_REASON}자)
              <span className="live-cb-modal__counter">
                {reason.trim().length}/{MIN_REASON}+
              </span>
            </span>
            <textarea
              ref={reasonRef}
              className="live-cb-modal__textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="예: 지수 급락 + 시스템 경보 반복 — 수동 중단"
              rows={3}
              disabled={busy}
              aria-invalid={!reasonOk && reason.length > 0}
            />
            {!reasonOk && reason.length > 0 && (
              <span className="live-cb-modal__hint live-cb-modal__hint--warn">
                감사 추적을 위해 {MIN_REASON}자 이상 입력하세요.
              </span>
            )}
          </label>

          <label className="live-cb-modal__check">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              disabled={busy}
            />
            <span>
              이 조치의 영향을 이해했으며, 내 판단 하에 발동하겠습니다.
            </span>
          </label>

          {error && (
            <div className="syslog-warning-banner" role="alert">
              {error}
            </div>
          )}
        </div>

        <footer className="live-cb-modal__actions">
          <button
            type="button"
            className="syslog-btn syslog-btn--ghost"
            onClick={onClose}
            disabled={busy}
          >
            취소
          </button>
          <button
            type="button"
            className={`live-cb-modal__submit live-cb-modal__submit--${tone}`}
            disabled={!canSubmit}
            onClick={() => onConfirm(action, reason.trim())}
          >
            {busy ? '발동 중…' : '발동'}
          </button>
        </footer>
      </div>
    </div>
  );
}
